import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import { consumerErrorMessage, readFragmentToken, verifyConsumerEmail } from "@/lib/consumerCommerce";

export default function SecureMacProVerifyEmail() {
  const location = useLocation();
  const [state, setState] = useState({ status: "working", message: "" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = readFragmentToken(location.hash);
    // Drop the one-time token from the address bar and history immediately.
    window.history.replaceState(null, "", location.pathname);
    if (!token) {
      setState({ status: "invalid", message: "" });
      return;
    }
    verifyConsumerEmail(token)
      .then(() => setState({ status: "verified", message: "" }))
      .catch((err) => {
        const invalid = err?.response?.status === 400;
        setState({ status: invalid ? "invalid" : "error", message: consumerErrorMessage(err) });
      });
  }, [location.hash, location.pathname]);

  return (
    <div className="bg-ink-50/40 text-ink-900">
      <SEO title="Verify your email — SecureShield Pro" description="Confirm your SecureShield Pro account email." noIndex />
      <section className="lf-section min-h-[60vh]">
        <div className="lf-container">
          <div className="mx-auto max-w-md border border-ink-100 bg-white p-7 md:p-9" data-testid="verify-email-page">
            <div className="lf-eyebrow">SecureShield Pro account</div>
            {state.status === "working" ? (
              <h1 className="mt-3 font-display text-2xl font-black" data-testid="verify-email-working">Verifying your email…</h1>
            ) : state.status === "verified" ? (
              <>
                <h1 className="mt-3 font-display text-2xl font-black" data-testid="verify-email-done">Email verified.</h1>
                <p className="mt-3 text-sm text-ink-600">Sign in to buy or download SecureShield Pro.</p>
                <Link className="lf-btn-primary mt-6 inline-flex" to="/account">Go to your account</Link>
              </>
            ) : state.status === "invalid" ? (
              <>
                <h1 className="mt-3 font-display text-2xl font-black" data-testid="verify-email-invalid">This link has expired or was already used.</h1>
                <p className="mt-3 text-sm text-ink-600">
                  Verification links work once and expire after 30 minutes. Create your account again with the same email
                  and we'll send a fresh link. If you've already verified, just sign in.
                </p>
                <Link className="lf-btn-primary mt-6 inline-flex" to="/account">Go to your account</Link>
              </>
            ) : (
              <>
                <h1 className="mt-3 font-display text-2xl font-black" data-testid="verify-email-error">We couldn't verify your email.</h1>
                <p className="mt-3 text-sm text-ink-600">{state.message}</p>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
